// src/components/BulkEditModal.tsx
import React, { useContext, useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import EditableField from "../EditableField";
import { SettingsContext } from "../../context/SettingsContext";
import { WorkerStatus, WorkerAvailabilityStatus } from "../../models/worker";

interface BulkEditModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * Called when user clicks Save. Passes the partial data
     * that should be applied to selected workers.
     */
    onSave: (data: any) => void;
}

/**
 * Example "Bulk Edit" modal that only lets you update
 * status + workerAvailability. Adjust as needed.
 */
const BulkEditModal: React.FC<BulkEditModalProps> = ({
                                                         open,
                                                         onClose,
                                                         onSave,
                                                     }) => {
    const { settings, loading: loadingSettings } = useContext(SettingsContext);

    const [status, setStatus] = useState<string>("");
    const [workerAvailability, setWorkerAvailability] = useState<string>("");

    useEffect(() => {
        // Reset fields whenever the modal is opened
        if (open) {
            setStatus("");
            setWorkerAvailability("");
        }
    }, [open]);

    const isFormValid = () => {
        // We might allow empty fields if user doesn't want to update them,
        // or we might require at least one field to be changed.
        // Adjust logic as needed.
        return status !== "" || workerAvailability !== "";
    };

    const handleSave = () => {
        // Build partial data. We only include fields that user selected.
        const data: any = {};
        if (status) {
            data.status = status as WorkerStatus;
        }
        if (workerAvailability) {
            data.workerAvailability = workerAvailability as WorkerAvailabilityStatus;
        }

        onSave(data);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Bulk Edit Workers</DialogTitle>
            <DialogContent dividers>
                {loadingSettings ? (
                    <Box display="flex" justifyContent="center" my={2}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Typography variant="body2">
                            Select fields you want to update for the selected workers. Leave
                            any field blank if you do not want to change it.
                        </Typography>

                        <EditableField
                            label="Status"
                            value={status}
                            editMode={true}
                            type="select"
                            options={settings?.workerStatuses || []}
                            onChange={(newValue) => setStatus(newValue)}
                        />
                        <EditableField
                            label="Work Availability"
                            value={workerAvailability}
                            editMode={true}
                            type="select"
                            options={settings?.availabilityStatuses || []}
                            onChange={(newValue) => setWorkerAvailability(newValue)}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} disabled={!isFormValid()}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkEditModal;
